/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { lazy, Suspense } from 'react';

import { FormattedMessage } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group';
import { jsx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutAllowedTargetAppKey,
	InvitedAppShortcutCollaborator,
} from '../../../../common/types';
import { messages } from '../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../common/utils/invited-app-shortcuts-utils';
import { useInvitedAppAnalytics } from '../../../../services/invited-app-shortcuts/invited-app-analytics';

import { InvitedAppCollaboratorsTooltip } from './invited-app-collaborators-tooltip';
import type { CollaboratorsSource } from './types';

// Lazy load the InvitedAppPopup component
const InvitedAppPopup = lazy(() =>
	import(/* webpackChunkName: "@atlassian/invited-app-popup" */ './invited-app-popup').then(
		(module) => ({
			default: module.InvitedAppPopup,
		}),
	),
);

export interface LazyInvitedAppPopupProps {
	/**
	 * Source of collaborator avatars. Legacy/experiment callers pass
	 * `{ kind: 'static', collaborators }`; productionisation callers pass
	 * `{ kind: 'lazy', app }`. The popup is mode-agnostic.
	 */
	collaboratorsSource: CollaboratorsSource;
	currentApsKey: InvitedAppShortcutAllowedApsKey;
	currentShortcutLink: string;
	/**
	 * Fired when the user clicks the accept-invite link. Legacy callers wire
	 * this to `resolverResult.registerActivation(app)`; productionisation
	 * callers can wire it to a no-op or to a different side effect.
	 *
	 * The popup also fires `fireLinkClickedAcceptInvite` analytics before
	 * calling `onAcceptInvite`.
	 */
	onAcceptInvite: (app: InvitedAppShortcutAllowedTargetAppKey) => void;
	onMouseEnterPopup: () => void;
	onMouseLeavePopup: () => void;
	trigger: React.MutableRefObject<HTMLAnchorElement | null>;
}

/**
 * Reads collaborators from the given source. In productionisation
 * (`kind: 'lazy'`) the popup currently does not render any avatars —
 * PUMA-1997 (`InvitedAppShortcutsContainerProdInner` +
 * `InvitedAppCollaboratorsLazyProvider`) will switch this to a body that
 * reads the shared lazy state for `collaboratorsSource.app`.
 */
const PopupCollaboratorsBody = ({
	collaboratorsSource,
	currentApsKey,
}: {
	collaboratorsSource: CollaboratorsSource;
	currentApsKey: InvitedAppShortcutAllowedApsKey;
}): JSX.Element | null => {
	if (collaboratorsSource.kind === 'lazy') {
		// TODO(PUMA-1997): render <PopupCollaboratorsLazyBody
		//   app={collaboratorsSource.app} /> which reads the shared lazy
		// state from `InvitedAppCollaboratorsLazyProvider`. Loading + error
		// fall back to no avatars; a real loading indicator is a follow-up.
		return null;
	}

	const collaborators = collaboratorsSource.collaborators;
	if (collaborators.length === 0) {
		return null;
	}

	return (
		<Inline grow="hug">
			<AvatarGroup
				data={collaborators.map((collaborator) => ({
					...collaborator,
					appearance: 'circle',
					size: 'medium',
				}))}
				maxCount={2}
				overrides={{
					MoreIndicator: {
						render: (Component, props) => (
							<InvitedAppCollaboratorsTooltip
								currentApsKey={currentApsKey}
								currentAppCollaborators={collaborators}
							>
								<Component {...props} isActive={false} onClick={() => {}} />
							</InvitedAppCollaboratorsTooltip>
						),
					},
				}}
			/>
		</Inline>
	);
};

/**
 * Returns the in-memory collaborator array for the static source, or `[]` for
 * the `lazy` source. Used to drive the popup body's main text and the
 * popup card width (`hasCollaborators`).
 */
const getStaticCollaborators = (
	collaboratorsSource: CollaboratorsSource,
): InvitedAppShortcutCollaborator[] =>
	collaboratorsSource.kind === 'static' ? collaboratorsSource.collaborators : [];

export const LazyInvitedAppPopup = ({
	collaboratorsSource,
	currentApsKey,
	currentShortcutLink,
	onAcceptInvite,
	onMouseEnterPopup,
	onMouseLeavePopup,
	trigger,
}: LazyInvitedAppPopupProps): JSX.Element => {
	const { fireLinkClickedAcceptInvite } = useInvitedAppAnalytics();

	const staticCollaborators = getStaticCollaborators(collaboratorsSource);
	const hasCollaborators = staticCollaborators.length > 0;

	return (
		<Suspense fallback={null}>
			<InvitedAppPopup
				trigger={trigger}
				onMouseEnterPopup={onMouseEnterPopup}
				onMouseLeavePopup={onMouseLeavePopup}
				currentApsKey={currentApsKey}
				hasCollaborators={hasCollaborators}
				collaboratorsCount={staticCollaborators.length}
			>
				<Inline>
					<Inline grow="fill">
						<Stack alignBlock="start" alignInline="start">
							<Text>
								{hasCollaborators ? (
									<FormattedMessage {...messages.popupMainTextWithCollaborators} />
								) : (
									<FormattedMessage {...messages.popupMainTextWithoutCollaborators} />
								)}
							</Text>
							<Link
								href={currentShortcutLink}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => {
									const app = getAppKeyByApsKey(currentApsKey);
									fireLinkClickedAcceptInvite({ app });
									onAcceptInvite(app);
								}}
							>
								<FormattedMessage {...messages.popupAcceptInviteButton} />
							</Link>
						</Stack>
					</Inline>
					<PopupCollaboratorsBody
						collaboratorsSource={collaboratorsSource}
						currentApsKey={currentApsKey}
					/>
				</Inline>
			</InvitedAppPopup>
		</Suspense>
	);
};
